import type { Placement } from '@popperjs/core';
import { isDefined } from '@sgme/fp';
import classNames from 'classnames';
import { InfoTooltip } from 'components/shared/InfoTooltip/InfoTooltip';
import type { IntlKey } from 'locales';
import { ChangeEvent, FC, ReactNode, useCallback } from 'react';
import Skeleton from 'react-loading-skeleton';

interface BorderedSwitchProps {
  id: string;
  label?: ReactNode;
  value: boolean;
  isDisabled?: boolean;
  tooltipKey?: IntlKey;
  tooltipPlacement?: Placement;
  className?: string;
  onValueChanged?: (value: boolean) => void;
  tabIndex?: number;
}

export const BorderedSwitch: FC<BorderedSwitchProps> = ({
  label,
  id,
  onValueChanged,
  value,
  isDisabled = false,
  tooltipKey,
  tooltipPlacement,
  className,
  tabIndex,
}) => {
  const onChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (onValueChanged !== undefined) {
        onValueChanged(e.target.checked);
      }
    },
    [onValueChanged],
  );

  return (
    <div
      className={classNames('d-flex justify-space-between align-items-center border px-3 py-2', className)}
      data-e2e={`switch-${id}`}
    >
      {isDefined(label) && <label htmlFor={id}>{label}</label>}
      {tooltipKey !== undefined && (
        <InfoTooltip
          id={`tooltip-${id}`}
          tooltipKey={tooltipKey}
          className="icon-sm"
          tooltipPlacement={tooltipPlacement}
        />
      )}
      <div className="ms-auto form-check form-switch form-check-edited">
        <input
          type="checkbox"
          className="form-check-input"
          id={id}
          onChange={onChange}
          checked={value}
          disabled={isDisabled}
          tabIndex={tabIndex}
        />
        <label className="form-check-label" htmlFor={id}></label>
      </div>
    </div>
  );
};

export const BorderedSwitchSkeleton: FC<{ className?: string }> = ({ className }) => {
  return (
    <div className={classNames('d-flex justify-space-between align-items-center border px-3 pt-3 pb-1', className)}>
      <label>
        <Skeleton width="10em" />
      </label>
      <div className="ms-auto form-check form-switch form-check-edited">
        <div className="me-2">
          <Skeleton width="3em" />
        </div>
      </div>
    </div>
  );
};
